import React from 'react';
import { Grid, Typography, Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootStateType } from '@Store';
import { titleCase } from '@Services/getTitleCase';
import { isActionAllowed } from '../../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { setInvoiceNumber } from '@Reducers/AdminShipment';
import { Carrier } from './constants';

function SingleAccountDetail({
  label,
  value,
  underline = false,
  navigateTo,
  isAccountHolder,
  invoiceNumber,
  purchaseInvoiceData,
}: {
  label: string;
  value: string;
  underline?: boolean;
  navigateTo?: string;
  isAccountHolder?: boolean;
  invoiceNumber?: number | null;
  purchaseInvoiceData?: any;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleClick = () => {
    if (navigateTo && navigateTo !== '') {
      if (navigateTo === '/purchase-invoices' && purchaseInvoiceData) {
        // Navigate to purchase-invoices with state (not query params)
        navigate(navigateTo, {
          state: {
            purchaseInvoiceNumber: purchaseInvoiceData.purchaseInvoiceNumber,
            carrierId: purchaseInvoiceData.carrierId,
          },
        });
      } else {
        dispatch(setInvoiceNumber({ isAccountHolder, invoiceNumber }));
        window.open(navigateTo, '_blank');
      }
    }
  };

  return (
    <Grid item sx={{ minWidth: label === 'Purchase Invoice No' ? 250 : 180 }}>
      <Typography className="font-semibold ">{label}</Typography>
      <Typography
        className={`font-semibold sb-text-gray ${underline ? 'underline color-p-main' : ''} ${
          navigateTo !== '' ? 'cursor-pointer' : ''
        } `}
        sx={{ display: 'block' }}
        onClick={handleClick}
      >
        {value}
      </Typography>
    </Grid>
  );
}

// New component specifically for Purchase Invoice display
function PurchaseInvoiceDetail({ purchaseInvoices, carriers = [] }: { purchaseInvoices: any[]; carriers?: Carrier[] }) {
  const navigate = useNavigate();

  const handleClick = (inv: any) => {
    navigate('/purchase-invoices', {
      state: {
        purchaseInvoiceNumber: inv.purchaseInvoiceNumber,
        carrierId: inv.carrierId,
      },
    });
  };

  const getCarrierName = (id: string) => {
    const found = carriers?.find((c: Carrier) => c._id === id);
    return found?.name || id; // fallback to id if no match found
  };

  if (purchaseInvoices.length === 0) {
    return (
      <Grid item sx={{ minWidth: 250 }}>
        <Typography className="font-semibold">Purchase Invoice No</Typography>
        <Typography className="font-semibold sb-text-gray">-</Typography>
      </Grid>
    );
  }

  return (
    <Grid item sx={{ minWidth: 250 }}>
      <Typography className="font-semibold">Purchase Invoice No</Typography>

      <Box className="cursor-pointer sb-text-gray">
        {purchaseInvoices.map((inv: any) => (
          <Typography
            key={inv._id}
            sx={{ display: 'block', fontSize: '14px', lineHeight: 1.4 }}
            className="font-semibold underline cursor-pointer"
            onClick={() => handleClick(inv)}
          >
            {inv.purchaseInvoiceNumber} - {getCarrierName(inv.carrierId)}
          </Typography>
        ))}
      </Box>
    </Grid>
  );
}

export default function AccountDetails(props: { carriers?: Carrier[] }) {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const userName = shipmentById?._userId.name || '';

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const isAccountHolderCreateInvoiceAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.View_Invoices_Account_holder.create_invoices,
    PERFORM_ACTION.write,
  );
  const isNonAccountHolderCreateInvoiceAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.View_Invoices_Non_Account_holder.create_invoices,
    PERFORM_ACTION.write,
  );

  const permissionNavigationgAllowed = shipmentById?._accountId?.isAccountHolder
    ? isAccountHolderCreateInvoiceAllowed
    : isNonAccountHolderCreateInvoiceAllowed;

  const purchaseInvoices = shipmentById?.purchaseInvoiceNumbers || [];

  return (
    <Grid container>
      <Grid
        container
        item
        xs={10}
        md={10}
        lg={10}
        alignItems="flex-start"
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          overflowX: 'auto',
          gap: 1.5,
        }}
      >
        <SingleAccountDetail
          label="Account no"
          value={shipmentById?._accountId?.isAccountHolder ? shipmentById?.accountNumber || '' : '-'}
        />

        <SingleAccountDetail
          label={shipmentById?._accountId?._company[0]?.name || '-----'}
          value={titleCase(userName)}
        />

        <SingleAccountDetail
          label="Sale invoice"
          value={shipmentById?.invoiceNumber ? `${shipmentById?.invoiceNumber}` : '-'}
          navigateTo={permissionNavigationgAllowed && shipmentById?.invoiceNumber ? '/invoice/view' : ''}
          isAccountHolder={shipmentById?._accountId?.isAccountHolder}
          underline={permissionNavigationgAllowed && shipmentById?.invoiceNumber ? true : false}
          invoiceNumber={shipmentById?.invoiceNumber}
        />
        <PurchaseInvoiceDetail purchaseInvoices={purchaseInvoices} carriers={props.carriers} />
      </Grid>
    </Grid>
  );
}
